import type { PlatformTimerSummary } from "@tdelivr/api-client/generated/workflow/delayed-command-timer.js";
import type { PlatformScheduleSummary } from "@tdelivr/api-client/generated/workflow/scheduler.js";
import type { PlatformWorkflowSummary } from "@tdelivr/api-client/generated/workflow/workflow-orchestrator.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, type BoardRow, type DetailField } from "./board.ts";

/**
 * What the workflow and schedules board does to the three listings it reads, away from any
 * rendering.
 *
 * Built to `16 WP15 Platform Machinery.dc.html`, page `workflow`, against **three** operations in
 * **three** services, published together as one cohort:
 *
 *   - WRK-01 `GET /v1/platform/workflows` under `workflows:platform:read` —
 *     [ADR 0061](../../../docs/adr/0061-a-platform-scoped-workflow-listing-is-a-separate-operation-and-the-execution-history-is-withheld.md);
 *   - WRK-02 `GET /v1/platform/schedules` under `schedules:platform:read` —
 *     [ADR 0062](../../../docs/adr/0062-a-platform-scoped-schedule-listing-is-a-separate-operation-and-the-command-body-stays-a-reference.md);
 *   - WRK-03 `GET /v1/platform/delayed-timers` under `delayed-timers:platform:read` —
 *     [ADR 0063](../../../docs/adr/0063-a-platform-scoped-delayed-command-listing-is-a-separate-operation-and-the-lease-token-is-the-reason.md).
 *
 * Each is its service's **first listing of any kind**, and its first public operation answering
 * anything but its own identity.
 *
 * ```
 * workflow:['Workflow and schedules','WRK-01…03',
 *   'Orchestrated workflows, timers and scheduled jobs.',
 *   [['Running','128','active','workflows'],['Stuck','2','human','past deadline'],
 *    ['Timers due','14','waiting','next 5 min'],['Failed today','1','failed','']],
 *   ['Workflow','Step','Due'],
 *   [['wf_payout_0928','await approval','—','human','waiting on a person'],
 *    ['wf_tenant_5HP2','provision search index','now','active','running'],
 *    ['wf_expire_docs','nightly 23:59','in 9 h','waiting','scheduled']],
 *   'info','Stuck workflows show the step they are waiting on and who can unblock them.']
 * ```
 *
 * ## Nothing on this platform models a workflow deadline, so the board's "needs a person" apparatus
 * is not drawn at all
 *
 * The design's `Stuck · 2 · past deadline` tile, its `human` row tint, its `waiting on a person`
 * badge and its `Open approval` detail action **all mean the same thing**: past a deadline. The
 * `Schedule a job` form collects `Deadline (text, e.g. 30 min)` with the helper "Stuck past this
 * shows as needing a person", which is where the deadline was supposed to come from.
 *
 * A search for `deadline` and `stuck` across every TypeScript, SQL and YAML file under
 * `services/workflow` finds nothing.
 * WRK-01 has `created_at`, `updated_at` and a status; WRK-02 has `next_fire_at`; WRK-03 has
 * `due_at`; none of them is a deadline a workflow can be past. ADR 0061 §Context and §8 record it
 * as modelling work belonging to whoever owns the workflow definition, not route work.
 *
 * **So none of the four is drawn, and the absence is deliberate rather than pending.** Inventing a
 * rule — "running for longer than an hour" — would put a decision in a browser about which of a
 * tenant's workflows need a person, which is a service's decision and not a console's. No row of
 * this board can reach the `human` class, which is also why `ui/RowDetail.tsx` draws no
 * `Open approval`: its action map is keyed by the class, and this board never produces that key.
 * **Q-218**, and it is the one blocking question in this cohort.
 *
 * ## One grid over three services, and the three are read separately
 *
 * ADR 0061 §7 decides it and gives four reasons: the browser can make all three reads, so ADR
 * 0030's "the read a channel cannot make" is not met; a composed listing would need a total
 * ordering across three databases that ADR 0027 cannot supply; one listing would force a service to
 * invent a common state vocabulary over three status enums; and a composer would concentrate three
 * cross-tenant scopes on one token. It also refuses to pretend ADR 0047 §3's first condition has
 * not fired — it has, by name, and whether EXP-005 is built is the console's decision rather than
 * three services'.
 *
 * The cost it names is paid here: the design draws one toolbar and one footer over what are three
 * independent cursors. Search, filter, sort and export apply to the merged page this board holds,
 * as `entitlements.ts` and CFG-02's flags board already do. Pagination is the different one, and
 * **Q-223**'s meanwhile is what `ui/WorkflowSchedules.tsx` does: each listing is paged to its own
 * full first page and the three are merged, with `boundedNote` saying so when any was truncated.
 *
 * ## What is withheld by each of the three, and why none of it costs this board a cell
 *
 * **WRK-01** (ADR 0061 §4) withholds `history[]` entire — a tenant's signal vocabulary, its
 * cancellation prose and its `vault://` payload references, which together describe one operator's
 * business process to the competitors sharing the board — the definition **checksum**, an
 * activity's **lease owner and expiry**, and `workflow_audit.actor_subject`.
 *
 * **WRK-02** (ADR 0062 §4) withholds **`payloadReference`**, the durable resolvable address of
 * another tenant's command body, plus the claim owner and expiry, the firing worker and the audit
 * subject. The statement never selects the stored `schedule` document at all, which is where the
 * reference lives.
 *
 * **WRK-03** (ADR 0063 §4) withholds **`leaseToken`**, and that one is a capability rather than a
 * description: anyone holding it can settle another tenant's timer, marking a command delivered
 * that was never delivered. Publishing it would have been publishing a write. Also the payload
 * reference, the lease owner and expiry, and the audit log's subject and details.
 *
 * ## The three cells the design draws that these three listings cannot fill
 *
 *   - **`Step` for a delayed command.** WRK-03 holds a `command_type` and a `status` and no step of
 *     any kind; the command type is already this row's first column, so repeating it under `Step`
 *     would be a wrong answer rather than a missing one. The design's own `—`, with the reason.
 *   - **`Step` as a human phrase.** The design writes `await approval`; WRK-01 holds
 *     `currentActivityType`, which is the tenant's own string (`await-approval`, `notify`) and is
 *     drawn verbatim. ADR 0061 §8, **Q-222**.
 *   - **`nightly 23:59`.** WRK-02 holds `59 23 * * *`. The prose is a client rendering and **no
 *     prose vocabulary is published anywhere** — not for `nightly`, not for an arbitrary
 *     expression — so the cron expression is drawn as published rather than rendered. Writing a
 *     cron-to-prose renderer here would be a rule in a browser deciding when a tenant's job runs,
 *     and a wrong one would misstate it. Part of **Q-219**'s family, and the same call
 *     `event-catalogue.ts` makes when it writes a UUID where the design writes `evt_88K2`.
 *
 * And the first column: the design writes `wf_payout_0928`, and every identifier behind these three
 * rows is a UUID. Each service has exactly one slug-shaped value — WRK-01's `definitionName`,
 * WRK-02's `triggerKey`, WRK-03's `commandType` — and that is what the column draws, with the
 * identifier in the detail panel. ADR 0062 §7 and ADR 0063 §7 each decide it for their own service;
 * WRK-01's is ADR 0061 §8's open **Q-219**, answered here the way its two siblings were.
 */

/**
 * The three scopes this board spends, one per owner.
 *
 * Three rather than one, because these are three services: ADR 0061 §2, ADR 0062 §2 and ADR 0063
 * §2 each name their own, resource first with the audience in the middle segment (ADR 0051 §2). No
 * existing scope is widened — `workflows:read`, `schedules:read` and `delayed-timers:read` each buy
 * a tenant-bounded by-identifier read, and ADR 0032 makes the consequence of widening one concrete.
 *
 * Literals in this application's source, which is what grants them: `channel-scopes.mjs` derives
 * `tdelivr-platform-console`'s realm grant from the scope strings this console's own files name
 * (ADR 0032's mechanism, ADR 0049's rule that a channel names only what its drawn screens spend).
 *
 * The literals live in `navigation.ts`, where all eighteen of this console's cross-boundary
 * scopes are collected so that "what can this console reach" is answerable from one file.
 * Re-exported here so a reader of this module finds them beside the reasoning, and so
 * `channel-scopes.mjs` sees one literal each rather than two.
 *
 * **Q-139 takes three more occasions here**, which is the concentration ADR 0061 §7's fourth reason
 * is about: nobody has decided who may hold a scope that reads across every tenant, and this one
 * page spends three of them.
 */
export { WORKFLOWS_SCOPE, SCHEDULES_SCOPE, DELAYED_TIMERS_SCOPE } from "./navigation.ts";

/** The design's own duration units: `2 min` in its header, `9 h` and `3 h` on its pages. */
const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/**
 * A span of milliseconds in the design's own units.
 *
 * A copy of `search-index.ts`'s function of the same name, for ADR 0021's reason in miniature: the
 * two boards are built in one change and share no module of their own, and a helper hoisted into
 * `board.ts` would be a change to a file five measured screens are pinned against. If a third board
 * needs it, it moves there.
 */
export function asSpan(ms: number): string {
  const span = Math.max(0, ms);
  if (span < MINUTE_MS) return `${String(Math.floor(span / 1000))} s`;
  if (span < HOUR_MS) return `${String(Math.floor(span / MINUTE_MS))} min`;
  if (span < DAY_MS) return `${String(Math.floor(span / HOUR_MS))} h`;
  return `${String(Math.floor(span / DAY_MS))} d`;
}

/**
 * The design's `Due` grammar: `now`, `in 9 h`, and the past it does not draw on this page.
 *
 * Two of the three are on this page — `now` on its second row and `in 9 h` on its third. The third
 * is `2 min ago`, which is this design file's own grammar for a past instant, taken from the
 * notifications dropdown its `hdrUI` builds. A board of real timers reaches the past constantly:
 * an overdue delayed command is precisely why somebody opened this page.
 *
 * Under a minute either way is `now`, which is what the design's second row means — a timer due at
 * this moment, not one due in forty seconds.
 */
export function asDue(instant: string, now: number): string {
  const at = Date.parse(instant);
  if (!Number.isFinite(at)) return instant;
  const delta = at - now;
  if (Math.abs(delta) < MINUTE_MS) return "now";
  return delta > 0 ? `in ${asSpan(delta)}` : `${asSpan(-delta)} ago`;
}

/**
 * An instant as the panel writes it: the design's date, then a 24-hour clock.
 *
 * UTC, for `board.ts`'s own recorded reason — every instant on these boards is published as UTC and
 * the design names no zone anywhere, which Q-24 records.
 */
const clock = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
  timeZone: "UTC",
});

export function asMoment(instant: string): string {
  const parsed = Date.parse(instant);
  if (!Number.isFinite(parsed)) return instant;
  return `${asDay(instant)} · ${clock.format(new Date(parsed))}`;
}

/** The three listings, each `undefined` when its service did not answer. */
export interface WorkflowListings {
  readonly workflows: readonly PlatformWorkflowSummary[] | undefined;
  readonly schedules: readonly PlatformScheduleSummary[] | undefined;
  readonly timers: readonly PlatformTimerSummary[] | undefined;
}

/**
 * Every badge word this board can show, grouped by the service whose lifecycle it belongs to.
 *
 * **Three of the design's words are its own and ten states have no designed row**, which is the
 * split `partner-api.ts` and `event-catalogue.ts` both record. The design draws three badges —
 * `waiting on a person`, `running`, `scheduled` — over three services with fourteen states between
 * them. Two of its three words are used; the third, `waiting on a person`, means "past a deadline"
 * and is not drawn at all (see this file's header). Every state the design did not draw takes its
 * own service's published word with the nearest of the token system's nine classes.
 */
export const WORKFLOW_STATES = [
  // WRK-01 `running | waiting | completed | failed | cancelled`
  "running",
  "waiting",
  // WRK-02 `active | paused | completed | cancelled`, where `active` is the design's `scheduled`
  // and WRK-03 `scheduled | leased | delivered | cancelled | dead_letter | outcome_unknown`
  "scheduled",
  "leased",
  "paused",
  "dead_letter",
  "outcome_unknown",
  "delivered",
  "completed",
  "failed",
  "cancelled",
] as const;

export type WorkflowState = (typeof WORKFLOW_STATES)[number];

interface RowState {
  readonly classKey: StateClassKey;
  readonly label: WorkflowState;
  readonly title: string;
}

/**
 * WRK-01's five states.
 *
 * | `status`    | class    | word        | whose word                                       |
 * | ----------- | -------- | ----------- | ------------------------------------------------ |
 * | `running`   | `active` | `running`   | the design's second row                          |
 * | `waiting`   | `waiting`| `waiting`   | WRK-01's own                                     |
 * | `completed` | `done`   | `completed` | WRK-01's own                                     |
 * | `failed`    | `failed` | `failed`    | WRK-01's own                                     |
 * | `cancelled` | `closed` | `cancelled` | WRK-01's own                                     |
 *
 * **`waiting` is the `waiting` class and never `human`, and that is the whole of Q-218 on one
 * line.** The design's first row is `human · waiting on a person`, and WRK-01's `waiting` covers a
 * workflow waiting on a timer exactly as much as one waiting on an approval — nothing in the
 * platform distinguishes them, because nothing models the deadline that would. The token system's
 * `waiting` class is "Waiting on others", which is true of both and claims neither.
 */
function workflowState(summary: PlatformWorkflowSummary): RowState {
  switch (summary.status) {
    case "running":
      return {
        classKey: "active",
        label: "running",
        title:
          "A workflow execution in progress. WRK-01's history is withheld from this listing: it carries a tenant's signal vocabulary, its cancellation prose and its vault payload references. ADR 0061 §4.",
      };
    case "waiting":
      return {
        classKey: "waiting",
        label: "waiting",
        title:
          "Waiting, which WRK-01 publishes for a workflow waiting on a timer and for one waiting on an approval alike. The design draws a separate waiting on a person badge for the second, and nothing on this platform models the deadline that would tell them apart. Q-218.",
      };
    case "completed":
      return { classKey: "done", label: "completed", title: "The execution finished." };
    case "failed":
      return {
        classKey: "failed",
        label: "failed",
        title:
          "The execution failed. Why is in the withheld history, which describes one operator's business process. ADR 0061 §4.",
      };
    case "cancelled":
      return {
        classKey: "closed",
        label: "cancelled",
        title:
          "Cancelled. The reason an operator wrote is in the withheld history: one to five hundred characters of free text. ADR 0061 §Context.",
      };
  }
}

/**
 * WRK-02's four states.
 *
 * | `status`    | class    | word        | whose word                        |
 * | ----------- | -------- | ----------- | --------------------------------- |
 * | `active`    | `waiting`| `scheduled` | **the design's third row**        |
 * | `paused`    | `idle`   | `paused`    | WRK-02's own                      |
 * | `completed` | `done`   | `completed` | WRK-02's own                      |
 * | `cancelled` | `closed` | `cancelled` | WRK-02's own                      |
 *
 * **`active` is drawn `scheduled`**, which ADR 0062 §7 settles as a client mapping rather than a
 * field: "The design says `scheduled`, this service says `active`." The design's own third row is
 * `wf_expire_docs · nightly 23:59 · in 9 h · waiting · scheduled`, so the class travels with the
 * word.
 *
 * **`paused` is `idle` rather than `closed`.** A paused schedule has not ended — it can be resumed
 * — so `closed`, which the token system glosses "Ended without success or failure", would say
 * something untrue. `idle` is "Exists; nothing has happened yet", which is what a paused schedule
 * is from the next firing's point of view. The word on the badge is WRK-02's own either way, so the
 * class is the only thing being chosen.
 */
function scheduleState(summary: PlatformScheduleSummary): RowState {
  switch (summary.status) {
    case "active":
      return {
        classKey: "waiting",
        label: "scheduled",
        title:
          "WRK-02 publishes active; the design's badge word for it is scheduled, which is a client mapping rather than a field. ADR 0062 §7. The command this schedule fires is a reference held outside this service and is withheld from this listing.",
      };
    case "paused":
      return {
        classKey: "idle",
        label: "paused",
        title:
          "Paused, so it will not fire until it is resumed. The design has no badge for this state; the word is WRK-02's own.",
      };
    case "completed":
      return {
        classKey: "done",
        label: "completed",
        title: "Every occurrence has fired. The design has no badge for this state.",
      };
    case "cancelled":
      return {
        classKey: "closed",
        label: "cancelled",
        title: "Cancelled, and next_fire_at is nulled. The design has no badge for this state.",
      };
  }
}

/**
 * WRK-03's six states.
 *
 * | `status`           | class     | word               | whose word                   |
 * | ------------------ | --------- | ------------------ | ---------------------------- |
 * | `scheduled`        | `waiting` | `scheduled`        | the design's third row       |
 * | `leased`           | `active`  | `leased`           | WRK-03's own                 |
 * | `delivered`        | `done`    | `delivered`        | WRK-03's own                 |
 * | `cancelled`        | `closed`  | `cancelled`        | WRK-03's own                 |
 * | `dead_letter`      | `failed`  | `dead_letter`      | WRK-03's own                 |
 * | `outcome_unknown`  | `unknown` | `outcome_unknown`  | WRK-03's own                 |
 *
 * ADR 0063 §7: "The board draws the stored status, which is honest" — the design's vocabulary has
 * no word for the last two. `outcome_unknown` takes the token system's `unknown` class, which is
 * the design's own dashed grey `?` badge and whose gloss is exactly this state: "borrows neither
 * success nor failure, because neither is true yet".
 */
function timerState(summary: PlatformTimerSummary): RowState {
  switch (summary.status) {
    case "scheduled":
      return {
        classKey: "waiting",
        label: "scheduled",
        title:
          "Waiting for its due instant. The command body is a reference WRK-03 never interprets and this listing never reads. ADR 0063 §Context.",
      };
    case "leased":
      return {
        classKey: "active",
        label: "leased",
        title:
          "A worker holds this timer. The lease owner, its expiry and the fencing token are withheld: the token is a capability to settle another tenant's command, not a description of one. ADR 0063 §4.",
      };
    case "delivered":
      return { classKey: "done", label: "delivered", title: "The command was delivered." };
    case "cancelled":
      return { classKey: "closed", label: "cancelled", title: "Cancelled before it fired." };
    case "dead_letter":
      return {
        classKey: "failed",
        label: "dead_letter",
        title:
          "Every attempt failed. The design's vocabulary has no word for this state, so WRK-03's own is drawn. ADR 0063 §7.",
      };
    case "outcome_unknown":
      return {
        classKey: "unknown",
        label: "outcome_unknown",
        title:
          "The delivery neither succeeded nor failed observably. The design's vocabulary has no word for this state, so WRK-03's own is drawn. ADR 0063 §7.",
      };
  }
}

/**
 * The design's `Due` cell, or its own `—`.
 *
 * `—` when the instant is null, which is a real absence on two of the three: a WRK-01 execution
 * with no scheduled timer has no `nextTimerFireAt`, and a cancelled or completed WRK-02 schedule
 * has no `nextFireAt`. The design's own first row draws `—` in this column.
 */
function dueCell(
  instant: string | null,
  now: number,
  absence: string,
): { text: string; absent?: true; title: string } {
  if (instant === null) return { text: "", absent: true, title: absence };
  return { text: asDue(instant, now), title: `${asMoment(instant)}.` };
}

/** One WRK-01 workflow execution, as the design's three columns and its panel. */
export function workflowRow(summary: PlatformWorkflowSummary, now: number): BoardRow {
  const state = workflowState(summary);
  return {
    /*
     * The owner, the tenant and the workflow. `(tenant_id, workflow_id)` is WRK-01's primary key and
     * this listing's cursor (ADR 0061 §5), prefixed with the owner because this grid merges three
     * services and a React key colliding across them would make one service's row replace another's.
     */
    id: `WRK-01:${summary.tenantId}:${summary.workflowId}`,
    cells: [
      {
        /*
         * `definitionName`, which is WRK-01's only slug-shaped value: `workflow_id` is a UUID and
         * the design writes `wf_payout_0928`. ADR 0061 §8 leaves this open as Q-219; its two
         * siblings answer the same question the same way — ADR 0062 §7 draws `trigger_key` as
         * itself and ADR 0063 §7 draws `commandType` with the identifier in the panel — so this
         * board answers all three consistently and puts the UUID below.
         *
         * Mono, which is the design's own rule reached by a different route: its grid sets a cell
         * in Geist Mono when it matches `/^(ten_|evt_|RPL|RSK|wf_|\/v1|key|offset)/`, and its own
         * cell here is `wf_payout_0928`. A definition name is the same kind of value under a
         * different spelling, which is what `board.ts` extends `mono` for.
         */
        text: summary.definitionName,
        mono: true,
        title: `WRK-01 workflow ${summary.workflowId} on tenant ${summary.tenantId}. The design writes a wf_ slug; the identifier is a UUID and definitionName is the slug-shaped value. ADR 0061 §8, Q-219.`,
      },
      summary.currentActivityType === null
        ? {
            text: "",
            absent: true,
            title:
              "No open activity, so there is no step to name. WRK-01 holds no current-step field: the laterals this listing is built on answer the open activity's type and nothing else. ADR 0061 §5.",
          }
        : {
            text: summary.currentActivityType,
            title: `The open activity's activity_type, drawn verbatim. The design writes a human phrase — await approval — and WRK-01 holds the tenant's own string. ADR 0061 §8, Q-222.`,
          },
      dueCell(
        summary.nextTimerFireAt,
        now,
        "No timer is scheduled for this execution, so nothing is due. The design's own first row draws — in this column.",
      ),
    ],
    state,
    /*
     * No tint. The design tints its `human` and `failed` classes, and no row of this board can be
     * `human`: the deadline that would decide it is modelled nowhere (Q-218). `--failRow` has no
     * rule in this console's stylesheet, which `event-catalogue.ts` records as part of Q-217.
     */
    detail: [
      { label: "Owner", value: "WRK-01 · workflow-orchestrator" },
      { label: "Tenant", value: summary.tenantId, mono: true },
      { label: "Workflow", value: summary.workflowId, mono: true },
      { label: "Definition", value: summary.definitionName, mono: true },
      { label: "Definition version", value: `v${String(summary.definitionVersion)}` },
      summary.currentActivityType === null
        ? { label: "Open activity", value: "", absent: true }
        : { label: "Open activity", value: summary.currentActivityType },
      summary.nextTimerFireAt === null
        ? { label: "Next timer", value: "", absent: true }
        : { label: "Next timer", value: asMoment(summary.nextTimerFireAt) },
    ],
    history: historyOf(summary.createdAt, summary.updatedAt, summary.version),
    glyph: PANEL_GLYPH,
  };
}

/** One WRK-02 schedule, as the design's three columns and its panel. */
export function scheduleRow(summary: PlatformScheduleSummary, now: number): BoardRow {
  const state = scheduleState(summary);
  return {
    // `(tenant_id, schedule_id)`, WRK-02's primary key and its cursor. ADR 0062 §5.
    id: `WRK-02:${summary.tenantId}:${summary.scheduleId}`,
    cells: [
      {
        // `trigger_key`, a slug-shaped `varchar(128)` and the closest value on this platform to the
        // design's `wf_expire_docs`, answered as itself with no `wf_` prefix invented. ADR 0062 §7.
        text: summary.triggerKey,
        mono: true,
        title: `WRK-02 schedule ${summary.scheduleId} on tenant ${summary.tenantId}. trigger_key is drawn as published; the design's wf_ prefix is its renderer's monospace trigger rather than a platform convention. ADR 0062 §7, Q-219.`,
      },
      recurrenceCell(summary),
      dueCell(
        summary.nextFireAt,
        now,
        "No next firing. WRK-02 nulls next_fire_at on cancellation and leaves it null once every occurrence of a one-shot has fired.",
      ),
    ],
    state,
    detail: [
      { label: "Owner", value: "WRK-02 · scheduler" },
      { label: "Tenant", value: summary.tenantId, mono: true },
      { label: "Schedule", value: summary.scheduleId, mono: true },
      { label: "Trigger", value: summary.triggerKey, mono: true },
      { label: "Type", value: summary.scheduleType },
      summary.cronExpression === null
        ? { label: "Cron", value: "", absent: true }
        : { label: "Cron", value: summary.cronExpression, mono: true },
      summary.fireAt === null
        ? { label: "Fires at", value: "", absent: true }
        : { label: "Fires at", value: asMoment(summary.fireAt) },
      { label: "Misfire policy", value: summary.misfirePolicy },
      summary.lastFiredAt === null
        ? { label: "Last fired", value: "", absent: true }
        : { label: "Last fired", value: asMoment(summary.lastFiredAt) },
    ],
    history: historyOf(summary.createdAt, summary.updatedAt, summary.version),
    glyph: PANEL_GLYPH,
  };
}

/**
 * The design's `Step` cell for a schedule, which is its recurrence.
 *
 * `59 23 * * *`, as published, in Geist Mono. **The design writes `nightly 23:59`**, which ADR 0062
 * §Context calls "that five-field expression rendered in prose by the client" — and no prose
 * vocabulary is published anywhere on this platform, for `nightly` or for anything else. A renderer
 * written here would be a rule in a browser deciding when a tenant's job runs, and it would have to
 * answer for `0,15,30,45 9-17 * * 1-5` as confidently as for the one expression the design happens
 * to draw.
 * So the published value is drawn, which is the call `event-catalogue.ts` makes when it writes a
 * UUID where the design writes `evt_88K2`. Q-219's family.
 *
 * A one-shot schedule has no cron expression at all — `scheduleType` is `one_shot` and `fireAt`
 * carries the single instant, which the `Due` column already draws. The cell says `one_shot`, which
 * is WRK-02's own word, rather than an em dash: the recurrence is known and is "exactly once".
 */
function recurrenceCell(summary: PlatformScheduleSummary): {
  text: string;
  mono?: true;
  title: string;
} {
  if (summary.cronExpression !== null) {
    return {
      text: summary.cronExpression,
      mono: true,
      title:
        "cron_expression as published. The design renders a five-field expression in prose — nightly 23:59 — and nothing on this platform publishes a prose vocabulary for one, so the expression is drawn rather than interpreted. ADR 0062 §Context, Q-219.",
    };
  }
  return {
    text: summary.scheduleType,
    title:
      "A one-shot schedule: it fires exactly once, at the instant in the Due column. WRK-02's own word for it, since there is no recurrence to render.",
  };
}

/** One WRK-03 delayed command timer, as the design's three columns and its panel. */
export function timerRow(summary: PlatformTimerSummary, now: number): BoardRow {
  const state = timerState(summary);
  return {
    // `(tenant_id, timer_id)`, WRK-03's primary key and its cursor. ADR 0063 §5.
    id: `WRK-03:${summary.tenantId}:${summary.timerId}`,
    cells: [
      {
        // `command_type`, a `^[a-z][a-z0-9_.-]{2,99}$` slug, which ADR 0063 §7 draws in the first
        // column with the UUID identifier in the detail panel.
        text: summary.commandType,
        mono: true,
        title: `WRK-03 timer ${summary.timerId} on tenant ${summary.tenantId}. command_type is the slug-shaped value; timer_id is a UUID. ADR 0063 §7, Q-219.`,
      },
      {
        text: "",
        absent: true,
        title:
          "Nothing models a delayed command's step: WRK-03 holds a command type and a status and no step of any kind, and the command type is already this row's first column. An owner is not a step and neither is a status, so the cell is left as the design's em dash rather than filled with a wrong answer.",
      },
      {
        text: asDue(summary.dueAt, now),
        title: `${asMoment(summary.dueAt)}. due_at is rewritten forward by a backoff on every retryable failure, which is why this listing is ordered by the primary key rather than by it. ADR 0063 §5.`,
      },
    ],
    state,
    detail: [
      { label: "Owner", value: "WRK-03 · delayed-command-timer" },
      { label: "Tenant", value: summary.tenantId, mono: true },
      { label: "Timer", value: summary.timerId, mono: true },
      { label: "Command", value: summary.commandType, mono: true },
      /*
       * `3 of 5`, which ADR 0063 §7 says is drawable and is the only honest thing a board can say
       * about a timer that keeps failing. These are **this platform's** delivery attempts and they
       * describe no tenant's business. The design's `retrying` wording beside it belongs to the
       * `Stuck` tile, which is the deadline nothing models.
       */
      {
        label: "Attempts",
        value: `${String(summary.attempts)} of ${String(summary.maxAttempts)}`,
      },
      { label: "Due", value: asMoment(summary.dueAt) },
    ],
    history: historyOf(summary.createdAt, summary.updatedAt, summary.version),
    glyph: PANEL_GLYPH,
  };
}

/**
 * The design's own glyph for this panel, which is `account_tree`.
 *
 * Its panel head renders `fm.icon`, and `fm.icon` comes from the page's entry in `FORMS`:
 * `FORMS.workflow[2]` is `account_tree`. The same glyph the rail uses for this page.
 */
const PANEL_GLYPH = "account_tree";

/**
 * The design's `History` block, with the three entries all three of these records genuinely have.
 *
 * `created_at`, `updated_at` and an integer `version` are columns of `workflow_executions`,
 * `schedules` and `delayed_timers` alike, so the block's shape is exactly supported on every row of
 * this merged grid — which is unusual, and worth saying, because the other platform boards draw at
 * least one of the three as an em dash.
 *
 * What none of the three publishes is **who**: the design writes `Created · W. Mutua` and
 * `Last changed · K. Otieno`, and `workflow_audit.actor_subject` and `schedule_audit.actor_subject`
 * hold identity subjects rather than display names and are withheld in any case (ADR 0061 §4, ADR
 * 0062 §Context). Q-107 is where the missing actor on every `History` block lives.
 */
function historyOf(createdAt: string, updatedAt: string, version: number): readonly DetailField[] {
  return [
    { label: "Created", value: asMoment(createdAt) },
    { label: "Last changed", value: asMoment(updatedAt) },
    { label: "Version", value: `v${String(version)}` },
  ];
}

/**
 * The merged grid, in the design's own row order.
 *
 * WRK-01's executions, then WRK-02's schedules, then WRK-03's timers — which is the order the
 * design's source chip names (`WRK-01…03`) and the order its own three sample rows appear in: two
 * WRK-01 executions and then a WRK-02 schedule. Within each listing the service's own order is kept
 * untouched, which is what `board.ts`'s `default` order promises.
 *
 * A listing that did not answer contributes nothing and blanks nothing. That is the composition
 * `ui/WorkflowSchedules.tsx` argues: one `Promise.all` would hide every workflow on the platform
 * because one of three services was unreachable.
 */
export function workflowRows(listings: WorkflowListings, now: number): readonly BoardRow[] {
  return [
    ...(listings.workflows ?? []).map((summary) => workflowRow(summary, now)),
    ...(listings.schedules ?? []).map((summary) => scheduleRow(summary, now)),
    ...(listings.timers ?? []).map((summary) => timerRow(summary, now)),
  ];
}

/** The states present on the rows the board is holding, so the filter offers nothing empty. */
export function statesPresent(rows: readonly BoardRow[]): readonly WorkflowState[] {
  const present = new Set(rows.map((row) => row.state.label));
  return WORKFLOW_STATES.filter((label) => present.has(label));
}

/**
 * What the board owes an operator about the three services behind it, in one sentence each.
 *
 * Four things, each said rather than implied:
 *
 *   - **how many rows came from each owner**, because the grid merges three record kinds under one
 *     set of column headings and nothing on a row says which service answered it until the panel is
 *     opened;
 *   - **how many tenants they came from**, because the page is a board of every workflow on the
 *     platform and cannot show that it is: nothing publishes how many tenants exist, so "across 5
 *     of 7 tenants" is unsayable and "from 3 tenants" is exactly true. `partner-api.ts`'s
 *     `tenantSpanNote` and `platform-audit.ts`'s are the same sentence for the same reason;
 *   - **which owner did not answer**, because two thirds of this grid can be missing without the
 *     board failing;
 *   - **that no deadline is modelled**, which is the design's `Stuck` tile, its row tint, its
 *     `waiting on a person` badge and its `Open approval` action all at once, and is the one
 *     absence a reader cannot discover by hovering a cell they did not think to hover.
 */
export function listingNote(listings: WorkflowListings): string | undefined {
  if (
    listings.workflows === undefined &&
    listings.schedules === undefined &&
    listings.timers === undefined
  ) {
    return undefined;
  }

  const counts: string[] = [];
  if (listings.workflows !== undefined) {
    counts.push(`${String(listings.workflows.length)} from WRK-01`);
  }
  if (listings.schedules !== undefined) {
    counts.push(`${String(listings.schedules.length)} from WRK-02`);
  }
  if (listings.timers !== undefined) counts.push(`${String(listings.timers.length)} from WRK-03`);

  const tenants = new Set([
    ...(listings.workflows ?? []).map((summary) => summary.tenantId),
    ...(listings.schedules ?? []).map((summary) => summary.tenantId),
    ...(listings.timers ?? []).map((summary) => summary.tenantId),
  ]).size;

  const span = `${counts.join(", ")}, from ${String(tenants)} tenant${tenants === 1 ? "" : "s"}. Three services answer this grid separately and the toolbar applies to the merged page. Q-223.`;

  const missing: string[] = [];
  if (listings.workflows === undefined) missing.push("WRK-01's workflows");
  if (listings.schedules === undefined) missing.push("WRK-02's schedules");
  if (listings.timers === undefined) missing.push("WRK-03's delayed timers");
  const gap = missing.length === 0 ? "" : ` ${missing.join(" and ")} did not answer.`;

  const deadline =
    " Nothing on this platform models a workflow deadline, so the design's Stuck tile and its waiting on a person badge are not drawn: a workflow waiting on a timer and one waiting on an approval are the same published state. Q-218.";

  return `${span}${gap}${deadline}`;
}

/**
 * Why this page's primary command is drawn and refused.
 *
 * **The design draws it**: `FORMS.workflow` is `['Schedule job','Schedule a job','account_tree','',
 * [['Job',[['name','Name',…],['cron','Schedule',…],['tenant','Tenant','select',
 * '|All tenants|ten_7Q2M|ten_3KD9',…],['dl','Deadline','text','e.g. 30 min',
 * 'Stuck past this shows as needing a person',…]]]],'Schedule','','']`. So its `formUI` returns a
 * visible button and its panel draws `Edit`; `CLAUDE.md` §1 forbids removing a control the design
 * draws as plainly as it forbids adding one, so it is drawn, disabled, with the reason on it.
 *
 * Three reasons, each sufficient on its own, and the third is the interesting one:
 *
 *   - **The form is WRK-02's `create`, not WRK-01's anything** — a name, a schedule and a tenant is
 *     a schedule (ADR 0061 §6) — and WRK-02 publishes no write under `/v1/platform`: create and the
 *     three transitions stay on `/internal/v1` and the router answers 404 on the platform-shaped
 *     path (ADR 0062 §6).
 *   - **Its `Tenant` select is the decision a cross-tenant write would have to take.** A platform
 *     operator choosing whose schedule to create is exactly what ADR 0062 §6 declines to publish.
 *   - **Its `Deadline` field has nothing to be written to.** "Stuck past this shows as needing a
 *     person" describes the deadline nothing on this platform models (Q-218), so even a public
 *     create could not accept this form as designed.
 */
export const SCHEDULE_REFUSAL =
  "The design's Schedule a job form is WRK-02's create, which is published on /internal/v1 only and which the gateway does not expose; its Tenant select is the cross-tenant write ADR 0062 §6 declines; and its Deadline field has no model anywhere on this platform to be written to. Q-218.";
